import type { PositionValue } from '@elfsight/configurator-contracts';
import type { FourValueTupleConvertable } from '@elfsight/widget-utils';
import { to4VTuple } from '@elfsight/widget-utils';

type BorderRadiusValue = number | string;
type BorderRadiusTuple = [
  BorderRadiusValue,
  BorderRadiusValue,
  BorderRadiusValue,
  BorderRadiusValue
];

export function computeBorderRadius(
  borderRadius: FourValueTupleConvertable<number | string>,
  position: PositionValue,
  verticalOffset: number,
  horizontalOffset: number,
  iconOnly?: boolean
): BorderRadiusTuple {
  const isCornerPosition = [
    'bottom-right',
    'bottom-left',
    'top-left',
    'top-right'
  ].includes(position);
  const isTopPosition = ['top-left', 'top', 'top-right'].includes(position);
  const isRightPosition = ['top-right', 'right', 'bottom-right'].includes(
    position
  );
  const isLeftPosition = ['bottom-left', 'left', 'top-left'].includes(position);

  const isCorner = !verticalOffset && !horizontalOffset;
  const isSticky = verticalOffset === 0 || horizontalOffset === 0;

  const [
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius
  ] = to4VTuple(borderRadius);

  const roundButtonMap: { [index: string]: BorderRadiusTuple } = {
    all: [
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomRightRadius,
      borderBottomLeftRadius
    ],
    'top-left': [0, 0, borderBottomRightRadius, 0],
    top: [0, 0, borderBottomRightRadius, borderBottomLeftRadius],
    'top-right': [0, 0, 0, borderBottomLeftRadius],
    right: [borderTopLeftRadius, 0, 0, borderBottomLeftRadius],
    'bottom-right': [borderTopLeftRadius, 0, 0, 0],
    bottom: [borderTopLeftRadius, borderTopRightRadius, 0, 0],
    'bottom-left': [0, borderTopRightRadius, 0, 0],
    left: [0, borderTopRightRadius, borderBottomRightRadius, 0]
  };

  // Radius for Corner positions
  if (isCornerPosition && isCorner) {
    return roundButtonMap[position];
  }

  // Radius for iconOnly button in Left and Right positions
  if (iconOnly && horizontalOffset === 0) {
    if (isLeftPosition) return roundButtonMap['left'];

    if (isRightPosition) return roundButtonMap['right'];
  }

  // Radius for Center positions that aren't sticky
  if (!isCornerPosition && (verticalOffset !== 0 || horizontalOffset !== 0)) {
    return roundButtonMap['all'];
  }

  // Radius for All positions that are sticky
  if (isSticky) {
    if (
      (isTopPosition && verticalOffset === 0) ||
      (isLeftPosition && horizontalOffset === 0)
    ) {
      return roundButtonMap['top'];
    }

    return roundButtonMap['bottom'];
  }

  return roundButtonMap['all'];
}
